import { useCallback, useEffect, useRef } from 'react';

type Room = {
  code: string;
  phase: string;
  ownerLogin: string;
  connection: 'idle' | 'connecting' | 'open' | 'closed';
  connectionAttempt: number;
  reconnectBlocked: boolean;
  retryGeneration: number;
  stopReconnect: () => void;
  tick: () => void;
  leave: () => void;
  connectSocket: () => void;
  refresh: () => Promise<void>;
};
export const useRoomFlow = (room: Room, login: string | undefined, ticking: boolean) => {
  const retries = useRef(0);
  const code = room.code,
    phase = room.phase,
    connection = room.connection;
  const connectionAttempt = room.connectionAttempt;
  const reconnectBlocked = room.reconnectBlocked;
  const retryGeneration = room.retryGeneration;
  useEffect(() => {
    retries.current = 0;
  }, [room, code, retryGeneration]);
  const recover = useCallback(async () => {
    if (room.reconnectBlocked) return;
    const recoveringCode = room.code;
    // The SDK refreshes an expired access token before opening another socket.
    if (room.connection === 'closed') await room.refresh();
    if (!room.reconnectBlocked && room.code === recoveringCode && room.ownerLogin === login) room.connectSocket();
  }, [room, login]);
  useEffect(() => () => room.leave(), [room]);
  useEffect(() => {
    if (room.ownerLogin && room.ownerLogin !== login) room.leave();
  }, [room, login]);
  useEffect(() => {
    if (!ticking) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    const resume = () => {
      clearInterval(timer);
      if (document.hidden) return;
      room.tick();
      timer = setInterval(() => room.tick(), 100);
    };
    resume();
    document.addEventListener('visibilitychange', resume);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [room, ticking]);
  useEffect(() => {
    if (!code || phase === 'setup' || phase === 'closed') return;
    const resume = () => {
      if (!document.hidden) void recover();
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    return () => {
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', resume);
    };
  }, [room, code, phase, recover]);
  useEffect(() => {
    if (connection !== 'open') return;
    // A successful handshake followed by an immediate close is still a failed recovery.
    const stable = setTimeout(() => {
      retries.current = 0;
    }, 10000);
    return () => clearTimeout(stable);
  }, [connection, connectionAttempt]);
  useEffect(() => {
    if (!code || phase === 'closed' || reconnectBlocked || connection !== 'closed') return;
    if (retries.current >= 5) {
      room.stopReconnect();
      return;
    }
    const timer = setTimeout(() => void recover(), 2000 * 2 ** Math.min(retries.current++, 3));
    return () => clearTimeout(timer);
  }, [room, code, phase, connection, connectionAttempt, recover, reconnectBlocked, retryGeneration]);
};
